import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import "./SimulationPage.css";
import mintLogo from "./assets/mint_logo.png";

const BACKEND =
  process.env.REACT_APP_BACKEND_URL || "https://ukpactb.themint.space";

const ADMIN_TOKEN_KEY = "adminToken";
const ACTIVE_SCENARIO_KEY = "simulationActiveScenario";
const ADDITIONAL_BUS_STOPS_SCENARIO = "additional-bus-stops";
const CAMERA_COUNTS_SCENARIO = "camera-counts";
// This is an independent application. Never pass the portal's admin JWT to it.
const CAMERA_COUNTS_URL =
  process.env.REACT_APP_CAMERA_COUNTS_URL ||
  "https://t-dt-1.tail0e42b5.ts.net/";

const SCENARIOS = [
  {
    number: "01",
    title: "Additional Bus Stops",
    description:
      "Compare the current 55B-1 return route with the proposed reroute containing three additional bus stops.",
    icon: "🚌",
    status: "available",
    accent: "green",
    key: ADDITIONAL_BUS_STOPS_SCENARIO,
  },
  {
    number: "02",
    title: "Camera Counts",
    description:
      "Explore the Ettaiyapuram Junction traffic simulation powered by live camera counts.",
    icon: "📷",
    status: "available",
    accent: "purple",
    key: CAMERA_COUNTS_SCENARIO,
  },
];

function getAdminToken() {
  try {
    return localStorage.getItem(ADMIN_TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

function setAdminToken(token) {
  try {
    if (token) {
      localStorage.setItem(ADMIN_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
    }
  } catch {
    // If storage is unavailable, the current in-memory page can still continue.
  }
}

function getSavedScenario() {
  try {
    const value = sessionStorage.getItem(ACTIVE_SCENARIO_KEY) || "";
    return SCENARIOS.some(
      (scenario) => scenario.key === value && scenario.status === "available"
    )
      ? value
      : null;
  } catch {
    return null;
  }
}

function saveScenario(value) {
  try {
    if (value) {
      sessionStorage.setItem(ACTIVE_SCENARIO_KEY, value);
    } else {
      sessionStorage.removeItem(ACTIVE_SCENARIO_KEY);
    }
  } catch {
    // Session persistence is helpful, but it is not required for auth.
  }
}

function isUnauthorized(error) {
  const status = error?.response?.status;
  return status === 401 || status === 403;
}

export default function SimulationPage({ navigate }) {
  const [authState, setAuthState] = useState("checking");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [verificationError, setVerificationError] = useState("");
  const [activeScenario, setActiveScenario] = useState(() => getSavedScenario());
  const [cameraReloadKey, setCameraReloadKey] = useState(0);
  const [cameraFrameLoaded, setCameraFrameLoaded] = useState(false);

  const clearScenario = useCallback(() => {
    saveScenario(null);
    setActiveScenario(null);
  }, []);

  const verifyAdminSession = useCallback(async () => {
    const token = getAdminToken();

    if (!token) {
      clearScenario();
      setVerificationError("");
      setAuthState("unauthenticated");
      return;
    }

    setAuthState("checking");
    setVerificationError("");

    try {
      // This endpoint is already protected by the existing admin JWT.
      // A 200 response confirms that the persisted admin token is still valid.
      await axios.get(`${BACKEND}/api/admin/drivers`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      });

      setAuthState("authenticated");
    } catch (error) {
      if (isUnauthorized(error)) {
        // Only an explicit 401/403 should destroy the persisted admin session.
        setAdminToken("");
        clearScenario();
        setVerificationError("");
        setAuthState("unauthenticated");
        return;
      }

      // IMPORTANT: do not delete a valid token just because the backend/network
      // was temporarily unreachable. This was one of the causes of the previous
      // "refresh sends me back to login" behaviour.
      setVerificationError(
        error?.response?.data?.message ||
          "Your admin session is still saved, but the server could not be reached to verify it. Please retry."
      );
      setAuthState("verification-error");
    }
  }, [clearScenario]);

  useEffect(() => {
    verifyAdminSession();
  }, [verifyAdminSession]);

  // Keep the simulation page in sync if adminToken changes in another tab.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== ADMIN_TOKEN_KEY) return;

      if (!event.newValue) {
        clearScenario();
        setAuthState("unauthenticated");
        return;
      }

      verifyAdminSession();
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [clearScenario, verifyAdminSession]);

  // The embedded HTML never performs its own backend login check anymore.
  // It can ask the authenticated React parent to close the scenario if it
  // notices that the token disappeared.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "UKPACT_SIM_AUTH_REQUIRED") return;

      clearScenario();
      setAuthState(getAdminToken() ? "checking" : "unauthenticated");

      if (getAdminToken()) {
        verifyAdminSession();
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [clearScenario, verifyAdminSession]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginError("");
    setVerificationError("");
    setLoginBusy(true);

    try {
      const response = await axios.post(
        `${BACKEND}/api/admin/login`,
        {
          username: username.trim(),
          password,
        },
        { timeout: 15000 }
      );

      if (!response.data?.success || !response.data?.token) {
        setLoginError("Invalid admin username or password.");
        return;
      }

      // The admin login endpoint itself has authenticated the user.
      // Persist exactly the same adminToken key used by the existing admin app.
      setAdminToken(response.data.token);
      setPassword("");
      setAuthState("authenticated");
    } catch (error) {
      setLoginError(
        error?.response?.data?.message ||
          "Login failed. Please check the admin username/password and backend connection."
      );
    } finally {
      setLoginBusy(false);
    }
  };

  const handleLogout = () => {
    setAdminToken("");
    clearScenario();
    setUsername("");
    setPassword("");
    setLoginError("");
    setVerificationError("");
    setAuthState("unauthenticated");
  };

  const openScenario = (scenarioKey) => {
    // Do not allow disabled/unknown scenario keys to be opened.
    if (
      !SCENARIOS.some(
        (scenario) =>
          scenario.key === scenarioKey && scenario.status === "available"
      )
    ) {
      return;
    }
    if (scenarioKey === CAMERA_COUNTS_SCENARIO) {
      setCameraFrameLoaded(false);
    }
    saveScenario(scenarioKey);
    setActiveScenario(scenarioKey);
  };

  const reloadCameraFrame = () => {
    setCameraFrameLoaded(false);
    setCameraReloadKey((current) => current + 1);
  };

  const goToPortal = () => {
    clearScenario();
    navigate("/");
  };

  if (authState === "checking") {
    return (
      <div className="simulation-portal simulation-auth-screen">
        <div className="simulation-bg-glow simulation-bg-glow-one" />
        <div className="simulation-bg-glow simulation-bg-glow-two" />

        <div className="simulation-auth-check-card">
          <img src={mintLogo} alt="MInT Lab" />
          <div className="simulation-spinner" />
          <h1>Checking admin access</h1>
          <p>Validating your saved simulation session…</p>
        </div>
      </div>
    );
  }

  if (authState === "verification-error") {
    return (
      <div className="simulation-portal simulation-auth-screen">
        <div className="simulation-bg-glow simulation-bg-glow-one" />
        <div className="simulation-bg-glow simulation-bg-glow-two" />

        <div className="simulation-auth-check-card simulation-auth-error-card">
          <img src={mintLogo} alt="MInT Lab" />
          <div className="simulation-auth-warning">!</div>
          <h1>Session saved</h1>
          <p>{verificationError}</p>

          <div className="simulation-auth-error-actions">
            <button type="button" onClick={verifyAdminSession}>
              Retry verification
            </button>
            <button type="button" className="danger" onClick={handleLogout}>
              Log out
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (authState !== "authenticated") {
    return (
      <div className="simulation-portal simulation-auth-screen">
        <div className="simulation-bg-glow simulation-bg-glow-one" />
        <div className="simulation-bg-glow simulation-bg-glow-two" />

        <header className="simulation-simple-header">
          <button
            type="button"
            className="simulation-back-link"
            onClick={() => navigate("/")}
          >
            ← Back to portal
          </button>
        </header>

        <main className="simulation-login-wrap">
          <section className="simulation-login-copy">
            <div className="simulation-login-brand">
              <img src={mintLogo} alt="MInT Lab" />
              <div>
                <strong>MInT Collaborative</strong>
                <span>IIT Madras | UKPACT</span>
              </div>
            </div>

            <div className="simulation-security-pill">Admin access only</div>
            <h1>Digital Twin Simulation</h1>
            <p>
              Simulation scenarios are restricted to administrators. Sign in with
              the same admin username and password used by the existing admin
              dashboard.
            </p>

            <div className="simulation-login-highlights">
              <span>✓ Backend-validated admin login</span>
              <span>✓ Admin session persists across page changes and refreshes</span>
              <span>✓ Protected scenario workspace</span>
            </div>
          </section>

          <section className="simulation-login-card">
            <div className="simulation-login-card-heading">
              <span>Restricted area</span>
              <h2>Admin sign in</h2>
              <p>Enter your administrator credentials to continue.</p>
            </div>

            <form onSubmit={handleLogin} className="simulation-login-form">
              <label>
                <span>Username</span>
                <input
                  autoFocus
                  autoComplete="username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  placeholder="Admin username"
                  required
                />
              </label>

              <label>
                <span>Password</span>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Admin password"
                  required
                />
              </label>

              {loginError && (
                <div className="simulation-login-error" role="alert">
                  {loginError}
                </div>
              )}

              <button
                type="submit"
                className="simulation-login-button"
                disabled={loginBusy}
              >
                {loginBusy ? "Signing in…" : "Sign in to Simulation"}
              </button>
            </form>
          </section>
        </main>
      </div>
    );
  }

  if (
    activeScenario === ADDITIONAL_BUS_STOPS_SCENARIO ||
    activeScenario === CAMERA_COUNTS_SCENARIO
  ) {
    const isCameraCounts = activeScenario === CAMERA_COUNTS_SCENARIO;

    return (
      <div
        className={`simulation-runner-page ${
          isCameraCounts ? "simulation-camera-runner" : ""
        }`}
      >
        <header className="simulation-runner-header">
          <div className="simulation-runner-left">
            <button
              type="button"
              className="simulation-runner-back"
              onClick={clearScenario}
            >
              ← Scenarios
            </button>

            <div className="simulation-runner-title">
              <span>Scenario {isCameraCounts ? "02" : "01"}</span>
              <strong>
                {isCameraCounts ? "Camera Counts" : "Additional Bus Stops"}
              </strong>
            </div>
          </div>

          <div className="simulation-runner-actions">
            <button
              type="button"
              onClick={goToPortal}
              className="simulation-runner-secondary"
            >
              Portal
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="simulation-runner-logout"
            >
              Log out
            </button>
          </div>
        </header>

        {isCameraCounts ? (
          <main className="simulation-camera-workspace">
            <div className="simulation-camera-toolbar">
              <div className="simulation-camera-heading">
                <div className="simulation-camera-icon" aria-hidden="true">
                  <svg
                    width="25"
                    height="25"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 4h-4l-2 3H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2-3Z" />
                    <circle cx="12" cy="13" r="3.5" />
                  </svg>
                </div>
                <div>
                  <div className="simulation-camera-eyebrow">
                    Live traffic digital twin
                  </div>
                  <h1>Ettaiyapuram Junction</h1>
                  <p>Camera-count-driven junction simulation</p>
                </div>
              </div>
              <div className="simulation-camera-tools">
                <span className="simulation-camera-source"><span aria-hidden="true" /> Embedded simulation</span>
                <button type="button" className="simulation-camera-reload" onClick={reloadCameraFrame}>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 11a8 8 0 1 0-2 6" /><path d="M20 4v7h-7" />
                  </svg>
                  Reload view
                </button>
              </div>
            </div>

            <div className="simulation-camera-viewer">
              {!cameraFrameLoaded && (
                <div className="simulation-camera-loading" role="status">
                  <div className="simulation-camera-loader" aria-hidden="true" />
                  <strong>Opening the junction simulation…</strong>
                  <span>Connecting to the external visualization</span>
                </div>
              )}
              <iframe
                key={cameraReloadKey}
                className="simulation-camera-frame"
                src={CAMERA_COUNTS_URL}
                title="Ettaiyapuram live junction"
                allow="fullscreen"
                allowFullScreen
                loading="eager"
                referrerPolicy="strict-origin-when-cross-origin"
                onLoad={() => setCameraFrameLoaded(true)}
              />
            </div>

            <footer className="simulation-camera-footer">
              <span>
                Simulation uses live camera counts; some traffic movements are
                estimated.
              </span>
            </footer>
          </main>
        ) : (
          <div className="simulation-frame-wrap">
            <iframe
              className="simulation-frame"
              src={`${process.env.PUBLIC_URL || ""}/additional-bus-stops.html?v=3`}
              title="Additional Bus Stops Simulation"
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="simulation-portal simulation-scenario-page">
      <div className="simulation-bg-glow simulation-bg-glow-one" />
      <div className="simulation-bg-glow simulation-bg-glow-two" />

      <header className="simulation-scenario-header">
        <div className="simulation-login-brand simulation-header-brand">
          <img src={mintLogo} alt="MInT Lab" />
          <div>
            <strong>MInT Collaborative</strong>
            <span>IIT Madras | UKPACT</span>
          </div>
        </div>

        <div className="simulation-header-actions">
          <button type="button" onClick={goToPortal}>
            Portal
          </button>
          <button type="button" onClick={handleLogout} className="danger">
            Log out
          </button>
        </div>
      </header>

      <main className="simulation-scenario-main">
        <div className="simulation-scenario-intro">
          <div className="simulation-security-pill">Authenticated admin workspace</div>
          <h1>Digital Twin Simulation</h1>
          <p>
            Choose a scenario to open the simulation. Only authenticated admin
            users can reach this workspace.
          </p>
        </div>

        <section className="simulation-scenario-grid">
          {SCENARIOS.map((scenario) => {
            const available = scenario.status === "available";

            return (
              <button
                type="button"
                key={scenario.number}
                className={`simulation-scenario-card ${scenario.accent} ${
                  available ? "is-available" : "is-disabled"
                }`}
                onClick={() => {
                  if (available) openScenario(scenario.key);
                }}
                disabled={!available}
              >
                <div className="simulation-scenario-topline">
                  <span className="simulation-scenario-number">
                    Scenario {scenario.number}
                  </span>
                  <span
                    className={`simulation-scenario-status ${
                      available ? "ready" : "soon"
                    }`}
                  >
                    {available ? "Ready" : "Coming soon"}
                  </span>
                </div>

                <div className="simulation-scenario-icon">{scenario.icon}</div>

                <div className="simulation-scenario-content">
                  <h2>{scenario.title}</h2>
                  <p>{scenario.description}</p>
                </div>

                <div className="simulation-scenario-footer">
                  <span>{available ? "Open simulation" : "Not yet available"}</span>
                  <b>{available ? "→" : "•"}</b>
                </div>
              </button>
            );
          })}
        </section>

        <div className="simulation-access-note">
          <span>🔒</span>
          <div>
            <strong>Admin-protected simulation</strong>
            <p>
              Admin authentication is validated by the existing backend and the
              session is kept in the same adminToken used by the admin dashboard.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
